import { getPlanStyle } from '../../utils/planStyles';
import React, { useState, useEffect } from 'react';
import { getBackendURL } from '../../utils/backendURL';
import axios from 'axios';
import { 
  Users, 
  Search, 
  Filter,
  Edit,
  Trash2,
  Crown,
  Mail,
  Calendar,
  Activity,
  ChevronLeft,
  ChevronRight,
  Download,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  X,
  Check,
  Trash,
  AlertTriangle
} from 'lucide-react';
import OpsDirectoryViews from './OpsDirectoryViews';
import useOpsDirectoryNavigation from './useOpsDirectoryNavigation';
import UserDirectoryTable from './UserDirectoryTable';
import OpsPlanFilter from './OpsPlanFilter';
import UserAccountTypeFilter from './UserAccountTypeFilter';
import useOpsUserDirectory from './useOpsUserDirectory';
import { showSuccess, showError, showWarning } from '../../utils/opsNotifications';

const UserManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Separate state for input display
  const [planFilter, setPlanFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState('deactivate'); // 'deactivate' or 'delete'
  
  // Batch operations state
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [showBatchDeleteModal, setShowBatchDeleteModal] = useState(false);
  const [showDomainDeleteModal, setShowDomainDeleteModal] = useState(false);
  const [domainToDelete, setDomainToDelete] = useState('');
  const [batchLoading, setBatchLoading] = useState(false);
  const [excludedEmails, setExcludedEmails] = useState('');
  const [domainExcludedEmails, setDomainExcludedEmails] = useState('');
  
  // Confirmation dialogs
  
  const usersPerPage = 10;

  const plans = ['free', 'starter', 'pro', 'business', 'enterprise'];
  const navigation = useOpsDirectoryNavigation();
  const fetchUserDetails = navigation.openUser;


  const { users, loading, error, setLoading, fetchUsers, accountType, setAccountType } =
    useOpsUserDirectory({ searchTerm, planFilter, currentPage, usersPerPage });

  // Debounced search effect - waits 500ms after user stops typing
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(delayedSearch);
  }, [searchInput]);

  const handleUserAction = (user, action) => {
    setUserToDelete(user);
    setDeleteType(action);
    setShowDeleteModal(true);
  };

  const confirmUserAction = async () => {
    if (!userToDelete) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      let response;

      if (deleteType === 'deactivate') {
        if (userToDelete.is_active) {
          response = await axios.put(
            `${getBackendURL()}/api/ops/users/${userToDelete.id}/deactivate`,
            {},
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
        } else {
          response = await axios.put(
            `${getBackendURL()}/api/ops/users/${userToDelete.id}/activate`,
            {},
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
        }
      } else if (deleteType === 'delete') {
        response = await axios.delete(
          `${getBackendURL()}/api/ops/users/${userToDelete.id}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }

      showSuccess(response.data.message);
      await fetchUsers(); // Refresh the list
      setShowDeleteModal(false);
      setUserToDelete(null);
    } catch (error) {
      console.error('Failed to perform user action:', error);
      let errorMessage = 'Failed to perform action';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        errorMessage = typeof detail === 'string' ? detail : 'Action failed';
      }
      showError(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchInput(value); // Update input display immediately
    // The debounced effect will update searchTerm after 500ms
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchTerm(searchInput); // Immediate search on Enter or button click
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handlePlanFilter = (plan) => {
    setPlanFilter(plan);
    setCurrentPage(1);
  };

  // Batch operations functions
  const toggleUserSelection = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedUsers.size === users.length && users.length > 0) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(users.map(user => user.id)));
    }
  };

  const handleBatchDelete = async () => {
    if (selectedUsers.size === 0) {
      showWarning('Please select users to delete');
      return;
    }

    try {
      setBatchLoading(true);
      const token = localStorage.getItem('admin_token');
      
      const response = await axios.delete(
        `${getBackendURL()}/api/ops/users/batch`,
        {
          data: { 
            user_ids: Array.from(selectedUsers),
            excluded_emails: excludedEmails 
          },
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const result = response.data;
      let message = result.message;
      
      if (result.excluded_count > 0) {
        message += `\n\nExcluded users: ${result.excluded_users.join(', ')}`;
      }

      showSuccess(message);
      setSelectedUsers(new Set());
      setExcludedEmails('');
      setShowBatchDeleteModal(false);
      await fetchUsers();
    } catch (error) {
      console.error('Failed to batch delete users:', error);
      showError(`Error: ${error.response?.data?.detail || 'Failed to batch delete users'}`);
    } finally {
      setBatchLoading(false);
    }
  };

  const handleDomainDelete = async () => {
    if (!domainToDelete.trim()) {
      showWarning('Please enter a domain');
      return;
    }

    try {
      setBatchLoading(true);
      const token = localStorage.getItem('admin_token');
      
      const response = await axios.delete(
        `${getBackendURL()}/api/ops/users/by-domain`,
        {
          data: { 
            domain: domainToDelete,
            excluded_emails: domainExcludedEmails 
          },
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const result = response.data;
      let message = result.message;
      
      if (result.excluded_count > 0) {
        message += `\n\nExcluded users: ${result.excluded_users.join(', ')}`;
      }

      showSuccess(`${message}\n\nDeleted users: ${result.deleted_count}`);
      setDomainToDelete('');
      setDomainExcludedEmails('');
      setShowDomainDeleteModal(false);
      await fetchUsers();
    } catch (error) {
      console.error('Failed to delete users by domain:', error);
      showError(`Error: ${error.response?.data?.detail || 'Failed to delete users by domain'}`);
    } finally {
      setBatchLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && navigation.isList) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
          <div className="h-10 bg-gray-200 rounded w-32 animate-pulse"></div>
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="admin-card p-4 animate-pulse">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error && navigation.isList) {
    return (
      <div className="admin-card p-6 border-red-200 bg-red-50">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
          <p className="text-red-800">{error}</p>
        </div>
        <button
          onClick={fetchUsers}
          className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <OpsDirectoryViews navigation={navigation} plans={plans} formatDate={formatDate}
      onRefresh={() => fetchUsers({ background: true })}>
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600">Manage Lexa.run user accounts and subscriptions</p>
          {selectedUsers.size > 0 && (
            <p className="text-sm text-blue-600 mt-1">
              {selectedUsers.size} user{selectedUsers.size !== 1 ? 's' : ''} selected
            </p>
          )}
        </div>
        <div className="flex items-center space-x-3">
          {/* Batch Operations */}
          {selectedUsers.size > 0 && (
            <button
              onClick={() => setShowBatchDeleteModal(true)}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Trash className="w-4 h-4 mr-2" />
              Delete Selected ({selectedUsers.size})
            </button>
          )}
          
          <button
            onClick={() => setShowDomainDeleteModal(true)}
            className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Delete by Domain
          </button>
          
          <button 
            onClick={fetchUsers}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
          <button className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-card p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <form onSubmit={handleSearchSubmit} className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email... (auto-search after 500ms or press Enter)"
                className="w-full pl-10 pr-20 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={searchInput}
                onChange={(e) => handleSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearchSubmit(e);
                  }
                }}
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </form>
          </div>

          <UserAccountTypeFilter value={accountType} onChange={value => {
            setCurrentPage(1);
            setAccountType(value);
          }} />
          <OpsPlanFilter value={planFilter} onChange={handlePlanFilter} />
        </div>
      </div>

      <UserDirectoryTable users={users} selectedUsers={selectedUsers} toggleSelectAll={toggleSelectAll}
        toggleUserSelection={toggleUserSelection} fetchUserDetails={fetchUserDetails}
        handleUserAction={handleUserAction} formatDate={formatDate} currentPage={currentPage}
        usersPerPage={usersPerPage} setCurrentPage={setCurrentPage} onOpenOrganization={navigation.openOrganization} />

      {/* Delete/Deactivate Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              {deleteType === 'delete' ? (
                <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
              ) : (
                <div className="flex-shrink-0 w-10 h-10 mx-auto bg-orange-100 rounded-full flex items-center justify-center">
                  <span className="text-xl">{userToDelete.is_active ? '🚫' : '✅'}</span>
                </div>
              )}
            </div>
            
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {deleteType === 'delete' ? 'Permanently Delete User' : 
                 (userToDelete.is_active ? 'Deactivate User' : 'Activate User')}
              </h3>
              
              <p className="text-sm text-gray-500 mb-4">
                {deleteType === 'delete' ? (
                  <>
                    <strong className="text-red-600">Warning:</strong> This will permanently delete{' '}
                    <strong>{userToDelete.email}</strong> and all their associated data including:
                    <ul className="mt-2 text-left list-disc list-inside">
                      <li>API test cases</li>
                      <li>Flow tests and executions</li>
                      <li>Team invitations</li>
                      <li>Password reset tokens</li>
                    </ul>
                    <strong className="text-red-600">This action cannot be undone!</strong>
                  </>
                ) : userToDelete.is_active ? (
                  <>
                    This will deactivate <strong>{userToDelete.email}</strong>.
                    The user will not be able to access their account, but their data will be preserved.
                    You can reactivate them later.
                  </>
                ) : (
                  <>
                    This will reactivate <strong>{userToDelete.email}</strong>.
                    The user will regain access to their account and all their data.
                  </>
                )}
              </p>
              
              <div className="flex space-x-3 justify-center">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setUserToDelete(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmUserAction}
                  disabled={loading}
                  className={`px-4 py-2 rounded-md text-sm font-medium text-white ${
                    deleteType === 'delete' 
                      ? 'bg-red-600 hover:bg-red-700' 
                      : userToDelete.is_active 
                        ? 'bg-orange-600 hover:bg-orange-700'
                        : 'bg-green-600 hover:bg-green-700'
                  } disabled:opacity-50`}
                >
                  {loading ? 'Processing...' : (
                    deleteType === 'delete' ? 'Delete Permanently' :
                    userToDelete.is_active ? 'Deactivate' : 'Activate'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Batch Delete Modal */}
      {showBatchDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                <Trash className="w-6 h-6 text-red-600" />
              </div>
            </div>
            
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Batch Delete Users
              </h3>
              
              <p className="text-sm text-gray-500 mb-4">
                <strong className="text-red-600">Warning:</strong> This will permanently delete{' '}
                <strong>{selectedUsers.size} selected user{selectedUsers.size !== 1 ? 's' : ''}</strong> and all their associated data including:
                <ul className="mt-2 text-left list-disc list-inside">
                  <li>API test cases</li>
                  <li>Flow tests and executions</li>
                  <li>Team invitations</li>
                  <li>Password reset tokens</li>
                </ul>
                <strong className="text-red-600">This action cannot be undone!</strong>
              </p>

              {/* Exclusion Field */}
              <div className="mb-4 text-left">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Exclude Emails (Optional)
                </label>
                <textarea
                  placeholder="Enter emails to exclude, separated by comma or semicolon:&#10;test@example.com, admin@testmind.ai; important@lexa.run"
                  value={excludedEmails}
                  onChange={(e) => setExcludedEmails(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  rows={3}
                  disabled={batchLoading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Users with these emails will be skipped during deletion. Use comma (,) or semicolon (;) to separate multiple emails.
                </p>
              </div>
              
              <div className="flex space-x-3 justify-center">
                <button
                  onClick={() => {
                    setShowBatchDeleteModal(false);
                    setSelectedUsers(new Set());
                    setExcludedEmails('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  disabled={batchLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleBatchDelete}
                  disabled={batchLoading}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-md text-sm font-medium text-white disabled:opacity-50"
                >
                  {batchLoading ? 'Deleting...' : `Delete ${selectedUsers.size} Users`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Domain Delete Modal */}
      {showDomainDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 mx-auto bg-orange-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Delete Users by Domain
              </h3>
              
              <p className="text-sm text-gray-500 mb-4">
                Enter a domain (e.g., "example.com" or "@testmind.ai") to delete all users with emails ending with that domain.
                <br /><br />
                <strong className="text-orange-600">Warning:</strong> This will permanently delete all matching users and their data.
              </p>
              
              <div className="mb-4 text-left">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Domain to Delete
                </label>
                <input
                  type="text"
                  placeholder="example.com or @testmind.ai"
                  value={domainToDelete}
                  onChange={(e) => setDomainToDelete(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  disabled={batchLoading}
                />
              </div>

              {/* Exclusion Field */}
              <div className="mb-4 text-left">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Exclude Specific Emails (Optional)
                </label>
                <textarea
                  placeholder="Enter emails to exclude from domain deletion:&#10;admin@testmind.ai, important@example.com; carlos@testmind.ai"
                  value={domainExcludedEmails}
                  onChange={(e) => setDomainExcludedEmails(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                  rows={3}
                  disabled={batchLoading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Specific emails to keep even if they match the domain. Use comma (,) or semicolon (;) to separate multiple emails.
                </p>
              </div>
              
              <div className="flex space-x-3 justify-center">
                <button
                  onClick={() => {
                    setShowDomainDeleteModal(false);
                    setDomainToDelete('');
                    setDomainExcludedEmails('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  disabled={batchLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDomainDelete}
                  disabled={batchLoading || !domainToDelete.trim()}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 rounded-md text-sm font-medium text-white disabled:opacity-50"
                >
                  {batchLoading ? 'Deleting...' : 'Delete by Domain'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
    </OpsDirectoryViews>
  );
};

export default UserManagement;
